import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import {
  CASE_GROUP_DIVIDER,
  CASE_GROUP_END_HEAD_CELL_IDS,
  CASE_GROUP_HEAD_CELL_IDS,
  COLUMN_COUNT,
  getBodyCellSx,
  getHeadCellSx,
  getRowCellSx,
  HEAD_CELL_BY_ID,
  HEAD_CELLS,
  MATTER_GROUP_CELL_SX,
  OVERDUE_ROW_HOVER_BG,
  ROW_NUMBER_WIDTH_PCT,
  TABLE_HEADER_CELL_SX,
  TASK_HEAD_CELL_IDS,
} from "./constants";
import { DescriptionCell } from "./DescriptionCell";
import { parseFunnel } from "./filtering";
import { UpcomingDeadlinesCell } from "./UpcomingDeadlinesCell";
import type { RowWithSpans } from "./types";

type LloFunnelTableProps = {
  loading: boolean;
  hasLoadedData: boolean;
  uniqueCaseCount: number;
  taskCount: number;
  totalTaskCount?: number;
  rows: RowWithSpans[];
  caseGroupCount: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
};

function isOverdue(dueDate?: string | null) {
  return !!dueDate && dayjs(dueDate).isBefore(dayjs(), "day");
}

function NegotiationCell({ value }: { value?: string | null }) {
  return (
    <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
      {value || "—"}
    </Typography>
  );
}

function FunnelCell({ funnel }: { funnel?: string | null }) {
  const { category, stage } = parseFunnel(funnel);

  if (!category) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }

  return (
    <Stack spacing={0.25} alignItems="flex-start">
      <Typography variant="body2" sx={{ fontSize: "0.8125rem", fontWeight: 500, lineHeight: 1.3 }}>
        {category}
      </Typography>
      {stage && (
        <Typography
          variant="body2"
          sx={{ fontSize: "0.75rem", color: "text.secondary", lineHeight: 1.3 }}
        >
          {stage}
        </Typography>
      )}
    </Stack>
  );
}

function DueDateCell({ dueDate, overdue }: { dueDate?: string | null; overdue: boolean }) {
  if (!dueDate) return <Typography variant="body2" color="text.disabled">—</Typography>;

  return (
    <Stack spacing={0.5} alignItems="flex-start">
      <Typography
        variant="body2"
        sx={{
          fontSize: "0.8125rem",
          fontWeight: overdue ? 700 : 500,
          color: overdue ? "error.dark" : "text.primary",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {dayjs(dueDate).format("MM/DD/YYYY")}
      </Typography>
      {overdue && (
        <Chip
          size="small"
          label="Overdue"
          color="error"
          sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700 }}
        />
      )}
    </Stack>
  );
}

export function LloFunnelTable({
  loading,
  hasLoadedData,
  uniqueCaseCount,
  taskCount,
  totalTaskCount,
  rows,
  caseGroupCount,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}: LloFunnelTableProps) {
  const isFiltered = totalTaskCount != null && totalTaskCount !== taskCount;

  return (
    <Paper
      variant="outlined"
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderColor: "rgba(15, 23, 42, 0.12)",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
      }}
    >
      {hasLoadedData && (
        <>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            useFlexGap
            spacing={1}
            sx={{ px: 2, py: 1.25, bgcolor: "grey.50" }}
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                label={`${uniqueCaseCount} case${uniqueCaseCount === 1 ? "" : "s"}`}
                sx={{ fontWeight: 600, bgcolor: "background.paper" }}
              />
              <Chip
                size="small"
                label={`${taskCount} task${taskCount === 1 ? "" : "s"}`}
                sx={{ fontWeight: 600, bgcolor: "background.paper" }}
              />
              {isFiltered && (
                <Typography variant="caption" color="text.secondary">
                  filtered from {totalTaskCount}
                </Typography>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Case columns stay grouped · overdue tasks are highlighted
            </Typography>
          </Stack>
          <Divider />
        </>
      )}

      <TableContainer sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}>
        <Table
          size="small"
          stickyHeader
          sx={{
            tableLayout: "fixed",
            width: "100%",
            "& .MuiTableCell-stickyHeader": {
              boxShadow: "0 1px 0 rgba(15, 23, 42, 0.12)",
            },
          }}
        >
          <colgroup>
            <col style={{ width: `${ROW_NUMBER_WIDTH_PCT}%` }} />
            {HEAD_CELLS.map((headCell) => (
              <col key={headCell.id} style={{ width: `${headCell.widthPct}%` }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...TABLE_HEADER_CELL_SX, width: `${ROW_NUMBER_WIDTH_PCT}%` }}>
                #
              </TableCell>
              {HEAD_CELLS.map((headCell) => (
                <TableCell key={headCell.id} sx={getHeadCellSx(headCell)}>
                  {headCell.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 10 }}>
                  <Stack spacing={1.5} alignItems="center">
                    <CircularProgress size={36} />
                    <Typography variant="body2" color="text.secondary">
                      Loading LLO funnel tasks…
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 8 }}>
                  <Stack spacing={0.75} alignItems="center">
                    <Typography variant="subtitle2" color="text.primary">
                      {hasLoadedData ? "No matching tasks" : "No report loaded"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {hasLoadedData
                        ? "Try adjusting or clearing filters."
                        : "Click Load Report to view LLO funnel tasks."}
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const overdue = isOverdue(row.dueDate);
                const caseDividerSx = row.isLastInCaseGroup ? CASE_GROUP_DIVIDER : {};
                const taskCellSx = { ...getRowCellSx(overdue), ...caseDividerSx };
                const groupCellSx = MATTER_GROUP_CELL_SX;

                return (
                  <TableRow
                    key={row.taskId}
                    hover
                    sx={{
                      "&.MuiTableRow-hover:hover": { bgcolor: "transparent" },
                      "&:hover .llo-task-cell": {
                        bgcolor: overdue ? OVERDUE_ROW_HOVER_BG : "rgba(241, 245, 249, 0.9)",
                      },
                    }}
                  >
                    {!row.skipCaseColumns ? (
                      <>
                        <TableCell rowSpan={row.groupRowSpan} sx={groupCellSx}>
                          <Box
                            sx={{
                              width: 26,
                              height: 26,
                              borderRadius: "6px",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: "rgba(15, 23, 42, 0.06)",
                              fontSize: "0.75rem",
                              fontWeight: 700,
                              color: "text.secondary",
                            }}
                          >
                            {row.rowNumber}
                          </Box>
                        </TableCell>
                        {CASE_GROUP_HEAD_CELL_IDS.map((columnId) => (
                          <TableCell
                            key={columnId}
                            rowSpan={row.groupRowSpan}
                            sx={{
                              ...groupCellSx,
                              ...getBodyCellSx(HEAD_CELL_BY_ID[columnId], {
                                bgcolor: groupCellSx.bgcolor,
                                ...(columnId === "caseName"
                                  ? { fontWeight: 600, color: "text.primary" }
                                  : {}),
                                ...(columnId === "lloExpiration"
                                  ? { whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }
                                  : {}),
                              }),
                            }}
                          >
                            {columnId === "caseName" && row.caseName}
                            {columnId === "raa" && (row.raa || "—")}
                            {columnId === "ap" && (row.ap || "—")}
                            {columnId === "lloExpiration" && (row.lloExpiration || "—")}
                            {columnId === "negotiation" && (
                              <NegotiationCell value={row.negotiation} />
                            )}
                          </TableCell>
                        ))}
                      </>
                    ) : null}
                    {TASK_HEAD_CELL_IDS.map((columnId) => {
                      if (columnId === "funnel") {
                        return (
                          <TableCell
                            key={columnId}
                            className="llo-task-cell"
                            sx={{ ...taskCellSx, ...getBodyCellSx(HEAD_CELL_BY_ID.funnel) }}
                          >
                            <FunnelCell funnel={row.funnel} />
                          </TableCell>
                        );
                      }

                      if (columnId === "dueDate") {
                        return (
                          <TableCell
                            key={columnId}
                            className="llo-task-cell"
                            sx={{
                              ...taskCellSx,
                              ...getBodyCellSx(HEAD_CELL_BY_ID.dueDate, {
                                whiteSpace: "nowrap",
                              }),
                            }}
                          >
                            <DueDateCell dueDate={row.dueDate} overdue={overdue} />
                          </TableCell>
                        );
                      }

                      if (columnId === "taskName") {
                        return (
                          <TableCell
                            key={columnId}
                            className="llo-task-cell"
                            sx={{
                              ...taskCellSx,
                              ...getBodyCellSx(HEAD_CELL_BY_ID.taskName, { fontWeight: 500 }),
                            }}
                          >
                            {row.taskName}
                          </TableCell>
                        );
                      }

                      if (columnId === "taskAssignee") {
                        return (
                          <TableCell
                            key={columnId}
                            className="llo-task-cell"
                            sx={{ ...taskCellSx, ...getBodyCellSx(HEAD_CELL_BY_ID.taskAssignee) }}
                          >
                            {row.taskAssignee || "—"}
                          </TableCell>
                        );
                      }

                      return (
                        <TableCell
                          key={columnId}
                          className="llo-task-cell"
                          sx={{ ...taskCellSx, ...getBodyCellSx(HEAD_CELL_BY_ID.description) }}
                        >
                          <DescriptionCell text={row.description} />
                        </TableCell>
                      );
                    })}
                    {!row.skipCaseColumns
                      ? CASE_GROUP_END_HEAD_CELL_IDS.map((columnId) => (
                          <TableCell
                            key={columnId}
                            rowSpan={row.groupRowSpan}
                            sx={{
                              ...groupCellSx,
                              ...getBodyCellSx(HEAD_CELL_BY_ID[columnId], {
                                bgcolor: groupCellSx.bgcolor,
                              }),
                            }}
                          >
                            <UpcomingDeadlinesCell text={row.upcomingDeadlines} />
                          </TableCell>
                        ))
                      : null}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider />
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={caseGroupCount}
        rowsPerPage={rowsPerPage}
        page={page}
        labelRowsPerPage="Cases per page"
        onPageChange={(_, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(e) => {
          onRowsPerPageChange(parseInt(e.target.value, 10));
        }}
        sx={{
          flexShrink: 0,
          minHeight: 52,
          bgcolor: "grey.50",
          ".MuiTablePagination-toolbar": { px: 2 },
          ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
            fontSize: "0.8125rem",
          },
        }}
      />
    </Paper>
  );
}
